import { useEffect, useRef, useState } from 'react'

export const DEFAULT_IDLE_TIMEOUT_MS = 5 * 60 * 1000

export const useIdleAndOnlineStatus = (
  autoPauseOnIdle = true,
  idleTimeoutMs: number = DEFAULT_IDLE_TIMEOUT_MS,
): { isIdle: boolean; isOffline: boolean } => {
  const [isIdle, setIsIdle] = useState(false)
  const [isOffline, setIsOffline] = useState(() => {
    if (typeof navigator === 'undefined') return false
    // navigator.onLine is true when online; treat false as offline
    return navigator.onLine === false
  })

  const idleTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const clearIdleTimer = () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
    }

    const scheduleIdleTimer = () => {
      clearIdleTimer()
      if (!autoPauseOnIdle) return
      idleTimerRef.current = window.setTimeout(() => {
        setIsIdle(true)
      }, idleTimeoutMs)
    }

    const handleActivity = () => {
      if (!autoPauseOnIdle) return
      setIsIdle(false)
      scheduleIdleTimer()
    }

    if (typeof window !== 'undefined' && autoPauseOnIdle) {
      // Initial schedule
      scheduleIdleTimer()
      const activityEvents: Array<keyof WindowEventMap> = [
        'mousemove',
        'mousedown',
        'keydown',
        'scroll',
        'touchstart',
      ]
      activityEvents.forEach((evt) =>
        window.addEventListener(evt, handleActivity, { passive: true }),
      )

      return () => {
        clearIdleTimer()
        activityEvents.forEach((evt) =>
          window.removeEventListener(evt, handleActivity),
        )
      }
    }

    return () => {
      clearIdleTimer()
    }
  }, [autoPauseOnIdle, idleTimeoutMs])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { isIdle, isOffline }
}
